// Umpires sub-tab — the card grid + add/edit modal for the standing
// umpire pool. Extracted from LeagueUmpires.jsx so the parent shell
// can compose it alongside the assignment tabs without carrying 500
// lines of grid logic.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, X, Pencil, Trash2, Gavel, Phone as PhoneIcon,
  Users as TeamIcon, UserRound, Upload, KeyRound
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import { EMPTY_UMPIRE } from "../shared";

function initials(u) {
  const f = (u.firstName || "").trim();
  const l = (u.lastName  || "").trim();
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
}

const CARD_TONES = ["gold", "cyan", "green", "violet", "orange", "red"];
function toneFor(u) {
  const seed = (u.firstName || "") + (u.lastName || "") + String(u.id || 0);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return CARD_TONES[h % CARD_TONES.length];
}

export default function UmpiresListTab({ umpires, loading, refresh, showAddInHeader }) {
  const toast = useToast();
  const [busy,      setBusy]      = useState(false);
  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(EMPTY_UMPIRE);

  // Bulk-upload state
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg,  setUploadMsg]  = useState("");
  const [preview,    setPreview]    = useState(null);
  const [importResult, setImportResult] = useState(null); // { inserted, createdAccounts }
  const fileRef = useRef(null);

  useEffect(() => {
    if (!showForm && !preview && !importResult) return;
    const onKey = e => {
      if (e.key !== "Escape") return;
      if (importResult) { setImportResult(null); return; }
      if (preview) { setPreview(null); return; }
      if (showForm) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm, preview, importResult]);

  // Parent (LeagueUmpires) surfaces "Add New Umpire" + "Upload Excel"
  // buttons in the page header. We expose the entry points via
  // window custom events so the top-level buttons can trigger our
  // modals without prop plumbing.
  useEffect(() => {
    const onOpenAdd    = () => openCreate();
    const onOpenUpload = () => fileRef.current?.click();
    window.addEventListener("league-umpire-open-create", onOpenAdd);
    window.addEventListener("league-umpire-open-upload", onOpenUpload);
    return () => {
      window.removeEventListener("league-umpire-open-create", onOpenAdd);
      window.removeEventListener("league-umpire-open-upload", onOpenUpload);
    };
  }, []);

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true); setUploadMsg(""); setPreview(null); setImportResult(null);
    try {
      const data = await api.previewUmpireUpload(file);
      setPreview(data);
    } catch (err) {
      setUploadMsg(err.message || "Preview failed.");
      toast.error(err.message || "Preview failed.");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  async function applyUpload() {
    if (!preview) return;
    setUploadBusy(true); setUploadMsg("");
    try {
      const r = await api.applyUmpireUpload(preview.token);
      setImportResult(r);
      setPreview(null);
      toast.success(`${r.inserted || 0} umpires imported.`);
      await refresh();
    } catch (err) {
      setUploadMsg(err.message || "Import failed.");
    } finally { setUploadBusy(false); }
  }

  function openCreate() { setEditingId(null); setForm(EMPTY_UMPIRE); setShowForm(true); }
  function openEdit(u) {
    setEditingId(u.id);
    setForm({
      firstName: u.firstName || "",
      lastName:  u.lastName  || "",
      teamName:  u.teamName  || "",
      email:     u.email     || "",
      phone:     u.phone     || "",
      isActive:  u.isActive !== false
    });
    setShowForm(true);
  }
  function close() { setShowForm(false); setEditingId(null); setForm(EMPTY_UMPIRE); }

  async function submit(e) {
    e.preventDefault();
    if (!form.firstName.trim()) { toast.error("First name is required."); return; }
    if (!form.lastName.trim())  { toast.error("Last name is required.");  return; }
    if (!form.email.trim())     { toast.error("Email is required so the umpire can log in."); return; }
    if (!form.phone.trim())     { toast.error("Phone number is required."); return; }
    setBusy(true);
    try {
      let result;
      if (editingId) {
        result = await api.updateUmpire(editingId, form);
        toast.success("Umpire updated.");
      } else {
        result = await api.createUmpire(form);
        toast.success("Umpire added.");
      }
      // If we just provisioned a fresh login for this umpire, surface
      // the credentials so the admin can share them out-of-band.
      if (result?.accountCreated && result.tempPassword) {
        setTimeout(() => {
          alert(
            `Login account created for ${form.firstName} ${form.lastName}.\n\n` +
            `Email:    ${form.email}\n` +
            `Password: ${result.tempPassword}\n\n` +
            `Please share these with the umpire — they can change the password after signing in.`
          );
        }, 100);
      }
      close();
      await refresh();
    } catch (err) { toast.error(err.message || "Save failed."); }
    finally { setBusy(false); }
  }

  async function remove(u) {
    const label = `${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`;
    if (!confirm(`Remove ${label} from the umpire pool?`)) return;
    try {
      await api.deleteUmpire(u.id);
      toast.success("Umpire removed.");
      await refresh();
    } catch (err) { toast.error(err.message || "Delete failed."); }
  }

  const total = useMemo(() => umpires.length, [umpires]);

  return (
    <>
      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1, flexWrap:"wrap"}}>
          <Gavel size={18}/>
          <span>All Umpires</span>
          <em style={{marginLeft:"auto", color:"var(--muted)", fontStyle:"normal", fontSize:12}}>
            {total} total
          </em>
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
        {!loading && umpires.length === 0 && (
          <div className="league-annEmpty">
            <Gavel size={28}/>
            <p>No umpires added yet. Use <b>Add New Umpire</b> to grow the pool.</p>
          </div>
        )}

        <div className="ump-grid">
          {umpires.map(u => {
            const tone = toneFor(u);
            const fullName = `${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`;
            return (
              <article key={u.id} className={`ump-card ump-card--${tone}`}>
                <div className="ump-card-glow" aria-hidden="true"/>
                <header className="ump-card-head">
                  <div className="ump-card-avatar">{initials(u)}</div>
                  <div className="ump-card-title">
                    <b>{fullName}</b>
                    <div style={{display:"flex", gap:6, flexWrap:"wrap", marginTop:3}}>
                      {u.teamName && (
                        <span className="ump-card-team">
                          <TeamIcon size={11}/> {u.teamName}
                        </span>
                      )}
                      {u.hasAccount && (
                        <span className="ump-card-loginPill" title="Umpire has an active login">
                          Login ready
                        </span>
                      )}
                    </div>
                  </div>
                </header>

                <div className="ump-card-body">
                  <div className="ump-card-row">
                    <span className="ump-card-row-label"><UserRound size={11}/> First</span>
                    <span className="ump-card-row-value">{u.firstName || "—"}</span>
                  </div>
                  <div className="ump-card-row">
                    <span className="ump-card-row-label"><UserRound size={11}/> Last</span>
                    <span className="ump-card-row-value">{u.lastName || "—"}</span>
                  </div>
                  <div className="ump-card-row">
                    <span className="ump-card-row-label"><TeamIcon size={11}/> Team</span>
                    <span className="ump-card-row-value">{u.teamName || "—"}</span>
                  </div>
                  <div className="ump-card-row">
                    <span className="ump-card-row-label"><PhoneIcon size={11}/> Phone</span>
                    <span className="ump-card-row-value">
                      {u.phone
                        ? <a href={`tel:${u.phone}`} className="ump-card-phoneLink">{u.phone}</a>
                        : "—"}
                    </span>
                  </div>
                </div>

                <footer className="ump-card-foot">
                  <button type="button" className="ump-card-btn ump-card-btn--update"
                    onClick={() => openEdit(u)}>
                    <Pencil size={13}/> Update
                  </button>
                  <button type="button" className="ump-card-btn ump-card-btn--delete"
                    onClick={() => remove(u)}>
                    <Trash2 size={13}/> Delete
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      </Panel>

      {/* Hidden file input driven by the header "Upload Excel" button */}
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        hidden
        onChange={onPickFile}
      />

      {/* Bulk-upload preview modal */}
      {preview && createPortal((
        <div className="modal" onClick={() => setPreview(null)}>
          <div className="modalInner league-previewModal" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={() => setPreview(null)} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={`Preview · ${preview.rows.length} row${preview.rows.length === 1 ? "" : "s"} from upload`}>
              <div className="league-previewCtrl">
                <span style={{color:"var(--muted)", fontSize:12}}>
                  Valid rows will be imported and each umpire will get a login account (default password <b>gabruz123</b>).
                </span>
                <button
                  type="button"
                  className="btn"
                  onClick={applyUpload}
                  disabled={uploadBusy || !preview.rows.some(r => r.status === "valid")}
                  style={{marginLeft:"auto"}}
                >
                  {uploadBusy
                    ? "Importing…"
                    : `Import ${preview.rows.filter(r => r.status === "valid").length} umpire${preview.rows.filter(r => r.status === "valid").length === 1 ? "" : "s"}`}
                </button>
              </div>
              {uploadMsg && <div className="err" style={{marginTop:8}}>{uploadMsg}</div>}

              <div className="league-previewTable">
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>First Name</th><th>Last Name</th>
                      <th>Team</th><th>Email</th><th>Phone</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map(r => (
                      <tr key={r.row} className={r.status === "error" ? "league-previewRow--err" : ""}>
                        <td>{r.row}</td>
                        <td>{r.firstName || "—"}</td>
                        <td>{r.lastName || "—"}</td>
                        <td>{r.teamName || "—"}</td>
                        <td>{r.email || "—"}</td>
                        <td>{r.phone || "—"}</td>
                        <td>
                          {r.status === "valid"
                            ? <span style={{color:"var(--green, #43d893)"}}>OK</span>
                            : <span style={{color:"var(--red, #ff6b6b)"}}>{r.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      ), document.body)}

      {/* Import summary — surfaces temp passwords for freshly-created accounts */}
      {importResult && createPortal((
        <div className="modal" onClick={() => setImportResult(null)}>
          <div className="modalInner league-previewModal" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={() => setImportResult(null)} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={
              <div style={{display:"flex", alignItems:"center", gap:8}}>
                <KeyRound size={16}/>
                <span>{importResult.inserted || 0} umpire{importResult.inserted === 1 ? "" : "s"} imported</span>
              </div>
            }>
              {(!importResult.createdAccounts || importResult.createdAccounts.length === 0) ? (
                <p style={{margin:0, color:"var(--muted)"}}>
                  All uploaded umpires already had accounts on file — nothing new to share.
                </p>
              ) : (
                <>
                  <p style={{margin:"0 0 10px", color:"var(--muted)", fontSize:13}}>
                    Share these credentials with each umpire so they can sign in and vote on polls.
                    They can change their password after first login.
                  </p>
                  <div className="league-previewTable">
                    <table>
                      <thead>
                        <tr><th>Name</th><th>Email</th><th>Temporary password</th></tr>
                      </thead>
                      <tbody>
                        {importResult.createdAccounts.map(a => (
                          <tr key={a.email}>
                            <td>{a.name}</td>
                            <td>{a.email}</td>
                            <td><code>{a.password}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div style={{display:"flex", justifyContent:"flex-end", marginTop:14}}>
                <button type="button" className="btn" onClick={() => setImportResult(null)}>
                  Done
                </button>
              </div>
            </Panel>
          </div>
        </div>
      ), document.body)}

      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Update Umpire" : "Add New Umpire"}>
              <form className="form" onSubmit={submit}>
                <div className="two">
                  <div>
                    <label>First Name *</label>
                    <input value={form.firstName}
                      onChange={e => setForm({ ...form, firstName: e.target.value })}
                      placeholder="First name" autoFocus/>
                  </div>
                  <div>
                    <label>Last Name *</label>
                    <input value={form.lastName}
                      onChange={e => setForm({ ...form, lastName: e.target.value })}
                      placeholder="Last name"/>
                  </div>
                </div>

                <label>Team Name</label>
                <input value={form.teamName}
                  onChange={e => setForm({ ...form, teamName: e.target.value })}
                  placeholder="e.g. Pitt Gabruz"/>

                <label>Email *</label>
                <input type="email" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="umpire@example.com"/>
                <small style={{color:"var(--muted)", fontSize:11, marginTop:-4}}>
                  Used for their login. If the email already belongs to a user we'll link to that account instead of creating a new one.
                </small>

                <label>Phone Number *</label>
                <input type="tel" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555 123 4567"/>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Add Umpire"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
